// #region IMPORT
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
/** Services Import */
import Commons from './../Services/Common/Common';
import {
  ParseCheckClientVersionResponse,
  RequestCheckClientVersion,
  ToManagerVersionRows,
} from '../Services/Common/ClientVersionCheck';
import {
  buildAdminManualPdfUrl,
  getDefaultAdminManualBaseUrl,
  getAdminManualPageKey,
  getAdminManualPdfFileName,
  probeAdminManualPdf,
  getPageTab,
} from '../Services/Manual/adminManualPdf';
import IndexedDb from '../Services/IndexedDb/IndexedDb';
import Global from '../Services/Common/Global';
/** Reducers Import */
import { DrawerActions } from '../Reducer/DrawerReducer';
/** Components Import */
import SearchBox from '../Components/Ui/SearchBox/SearchBox';
/** MUI Import */
import { styled, useTheme } from '@mui/material/styles';
import {
  Box,
  Drawer as MuiDrawer,
  Dialog as MuiDialog,
  CssBaseline,
  AppBar as MuiAppBar,
  Toolbar,
  List,
  Typography,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Avatar,
  Menu,
  MenuItem,
  Breadcrumbs,
  Tooltip,
  tooltipClasses,
  Popper,
  MenuList,
  TextField,
} from '@mui/material';
import {
  AdUnits as AdUnitsIcon,
  Help as HelpIcon,
  Logout as LogoutIcon,
  History as HistoryIcon,
  MenuBook as MenuBookIcon,
  AutoStories as AutoStoriesIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import Icon from '../Assets/Icon/CustomIcons';
/** Images Import */
import LogoImgHome from '../Assets/Images/SafeOn_Logo(투명)_003 1.svg';
import LogoImgPage from '../Assets/Images/SafeOn_Logo.svg';
import avata from '../Assets/Images/avata.png';
import { showSnackbar } from '../Reducer/SnackbarReducer';
import { BaseInfoActions } from '../Reducer/BaseInfoReducer';
import {
  Button,
  Checkbox,
  Combo,
  Dialog,
  OperationHistoryDialog,
  NewContractGuideDialog,
} from '../Components/MUIs';
import { getReceptionPayload, initFcm } from '../firebase';
import { LargeTextCellEditor } from 'ag-grid-community';
import { showInAppNoti } from '../Reducer/InAppNotiReducer';
import { useGetHomeDataMutation } from '../Scripts/Home/Home_QUERY';
import { apiRequest } from '../Services/Common/ApiRequest';
import { mapParamsBySchema } from '../util';
import { handleQuerySuccess } from '../hooks/useQuriesSuccess';
import { PLA_BASIC_Schema } from '../Scripts/BasicInfo/Plant/PLA_BASIC/PLA_BASIC_Schema';
import { MEM_BASIC_Schema } from '../Scripts/BasicInfo/Member/MEM_BASIC/MEM_BASIC_Schema';
import { DEP_BASIC_Schema } from '../Scripts/BasicInfo/Department/DEP_BASIC/DEP_BASIC_Schema';
import { GON_BASIC_Schema } from '../Scripts/BasicInfo/Gongjong/GON_BASIC/GON_BASIC_Schema';
import dayjs from 'dayjs';
import { FetchAdminCompanyMasterRows, IsAdminUser } from '../hooks/adminCompanyMaster';
// #endregion

// #region 상수
const appbarHeight = '80px';
const drawerOpenWidth = '230px';
const drawerCloseWidth = '80px';
const MAX_DEP_FOR_PROCESS_SCAN = 48;

// 아이콘을 키-값 형태로 매핑
const iconMap = {
  Home: <Icon.Dashboard />,
  BasicInfo: <Icon.Database />,
  Schedule: <Icon.Schedule />,
  Aicall: <Icon.AiCall />,
  AiDocumentManager: <Icon.AiDocs />,
  AicallManager: <Icon.AiEdit />,
  AicallStatus: <Icon.AiMessage />,
  NoticeBoard: <Icon.SpeakerPhone />,
  PopupManagement: <AdUnitsIcon sx={{ fontSize: 30 }} />,
  SafetySystem: <Icon.Safety />,
  RiskEvaluation: <Icon.AlertOctagon />,
  SafeWork: <Icon.Construction />,
  //TRA_BASIC: <Icon.Education />,
  Training: <Icon.Education />,
  HealthManagement: <Icon.Health />,
  Accidents: <Icon.Danger />,
  RiskNearMiss: <Icon.SafetyWhistle />,
  SuggestionList: <Icon.SafetyWhistle />,
  HelpDesk: <HelpIcon />,
  Board: <Icon.MailBox />,
  DashBoard: <Icon.Dashboard2 />,
  SystemInfo: <Icon.Setting />,
  Logout: <Icon.Logout />,
};

// #endregion

//#region UI 디자인
const Center = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme }) => ({
    flexGrow: 1,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: drawerCloseWidth,
    height: `calc(100vh - ${appbarHeight})`,
  })
);

const openedMixin = (theme) => ({
  width: drawerOpenWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  backgroundColor: '#2EB450',
  borderRight: 'none',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: drawerCloseWidth,
  borderRight: 'none',
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  height: appbarHeight, // mixin 이후 덮어쓰기
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  variants: [
    {
      props: ({ open }) => open,
      style: {
        marginLeft: drawerOpenWidth,
        transition: theme.transitions.create(['width', 'margin'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
}));

const DrawerBody = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme }) => ({
  width: drawerOpenWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
      },
    },
  ],
}));

const GreenTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: '#2CA900',
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#2CA900',
  },
}));

const ScrollWrapper = styled('div')(({ showScroll }) => ({
  overflowY: showScroll ? 'auto' : 'hidden',
  overflowX: 'hidden',
  transition: 'overflow 0.3s ease-in-out',
  scrollbarWidth: 'thin',
  scrollbarColor: '#28A745 transparent',
  '&::-webkit-scrollbar': {
    width: '7px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#aaa',
    borderRadius: '7px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
}));
//#endregion

export default function Drawer() {
  const NEW_CONTRACT_GUIDE_HIDE_KEY = 'newContractGuideHideUntil';
  // #region 변수
  // 현재 URL이 "/"인지 확인
  const dispatch = useDispatch(); // Dispatch 사용을 위한 저장
  const drawerStates = useSelector((state) => state.drawers); // Drawer Redux 호출
  const {
    userInfo,
    menuInfo,
    fcmReceptionPayload,
    custInfo,
    lanInfo,
    riskWorkerOpinionMenuActive,
  } = useSelector((state) => state.BaseInfo);
  const searchInfo = useSelector((state) => state.SearchInfo);

  const location = useLocation();
  const navigate = useNavigate(); // 화면 이동을 위한 Navigate 저장
  /** ADMIN: 메뉴 이동마다 GetInfo 재호출 방지 (로그인 세션당 1회) */
  const adminBaseInfoLoadedRef = useRef({ userId: '', loaded: false });
  const [modalToggle, setModalToggle] = useState(false); // 문의하기 모달 상태
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false); // 처리 내역 히스토리 다이얼로그 상태
  const [newContractGuideOpen, setNewContractGuideOpen] = useState(false); // 신규 계약 운영 가이드 (관리자 공통)
  const [newContractGuideHideToday, setNewContractGuideHideToday] =
    useState(false);
  const [manualUnavailableOpen, setManualUnavailableOpen] = useState(false);
  const [manualUnavailableMessage, setManualUnavailableMessage] = useState('');
  const [manualChecking, setManualChecking] = useState(false);
  const [qnaInput, setQnaInput] = useState({
    sendEmail: '',
    type: 'itemQna',
    title: '',
    id: Commons.DecryptoAES(userInfo?.userid) || '',
    name: userInfo?.userNm || '',
    contact: userInfo?.userTel || '',
    content: '',
  });
  const [checkedTerms, setCheckedTerms] = useState({
    terms1: false,
  });
  const [largeMenuArray, setLargeMenuArray] = useState([]); // Drawer large Menu Array
  const [mediumMenuArray, setMediumMenuArray] = useState([]); // Drawer Medium Menu Array
  const [loading, setLoading] = useState(false);
  const [anchorEl2, setAnchorEl2] = useState(null);
  const [hoveredMenuId, setHoveredMenuId] = useState(null);
  const [activeMenuId, setActiveMenuId] = useState(() => {
    const path = location.pathname.split('/')[1];
    return path === 'Home' ? 'W20001' : path;
  });
  const [activeSubMenuId, setActiveSubMenuId] = useState(() => {
    const path = location.pathname.split('/')[2];
    return path ?? null;
  });
  const [imageFile, setImageFile] = useState({
    //이미지 설정
    mapFiles: '', //파일 String
    mapImage: '', // 파일 정보
  });
  const [description, setDescription] = useState([]); //메뉴 설명
  const [delayedDrawerOpen, setDelayedDrawerOpen] = useState(false); // popper 열리는 시간 조절
  const [showScroll, setShowScroll] = useState(false);
  const [version, setVersion] = useState('');
  const [findDialog, setFindDialog] = useState(false);
  const [inputs, setInputs] = useState({
    currentPassword: '',
    password: '',
    passwordConfirm: '',
  });
  const [capsLockFlag, setCapsLockFlag] = useState(false);
  const [searchTerm, setSearchTerm] = useState(''); // 메뉴 검색

  const drawerRef = useRef();
  const anchorRefs = useRef({});
  const timeoutRef = useRef(null);
  const guideAutoShownRef = useRef(false);

  const open2 = Boolean(anchorEl2);
  const isDashboard = location.pathname === '/Home';

  const openAdminManual = useCallback(async () => {
    const base =
      process.env.REACT_APP_ADMIN_MANUAL_BASE_URL?.trim() ||
      getDefaultAdminManualBaseUrl();
    const { pathname } = location;
    const pageKey = getAdminManualPageKey(pathname);
    const pdfUrl = buildAdminManualPdfUrl(base, pathname);
    if (!pdfUrl) {
      setManualUnavailableMessage(
        '매뉴얼 주소를 만들 수 없습니다. REACT_APP_FILE_URL 또는 REACT_APP_ADMIN_MANUAL_BASE_URL 을 설정해 주세요.'
      );
      setManualUnavailableOpen(true);
      return;
    }
    setManualChecking(true);
    try {
      const probe = await probeAdminManualPdf(pdfUrl);
      if (probe.ok) {
        const viewerUrl = `${window.location.origin}/manual-viewer?url=${encodeURIComponent(pdfUrl)}`;
        window.open(
          viewerUrl,
          '_blank',
          'width=1400,height=960,resizable=yes,scrollbars=yes'
        );
      } else if (probe.reason === 'not_found') {
        const SAM_TAB_LABEL = { 0: '작업계획서', 1: '안전작업 허가서' };
        const baseLabel = pageKey || '로그인·회원가입';
        const tabLabel =
          pageKey === 'SafetyWorkManagement'
            ? ` > ${SAM_TAB_LABEL[getPageTab('SafetyWorkManagement')] ?? ''}`
            : '';
        const label = `${baseLabel}${tabLabel}`;
        const fileName = getAdminManualPdfFileName(pathname);
        setManualUnavailableMessage(
          `이 화면(${label})에 대한 매뉴얼 PDF가 등록되어 있지 않습니다.\n\n서버 파일명: ${fileName}`
        );
        setManualUnavailableOpen(true);
      } else {
        setManualUnavailableMessage(
          '매뉴얼 PDF에 접속할 수 없습니다. 네트워크 또는 서버 CORS 설정을 확인해 주세요.'
        );
        setManualUnavailableOpen(true);
      }
    } finally {
      setManualChecking(false);
    }
  }, [location.pathname]);

  const { run: RunGetHomeData } = useGetHomeDataMutation({
    onSuccess: (rows, response) => {
      const res = response ?? {};
      if (res.isSuccess === false || res.IsSuccess === false) {
        dispatch(BaseInfoActions.setRiskWorkerOpinionMenuActive(false));
        return;
      }
      let list = [];
      if (Array.isArray(res.data)) list = res.data;
      else if (Array.isArray(res.Data)) list = res.Data;
      else if (Array.isArray(rows) && rows.length) list = rows;
      const home = list[0];
      const active = !!(
        home?.riskWorkerOpinionMenuActive ?? home?.RiskWorkerOpinionMenuActive
      );
      dispatch(BaseInfoActions.setRiskWorkerOpinionMenuActive(active));
    },
  });
  // #endregion

  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current);
    setShowScroll(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setShowScroll(false);
    }, 2000);
  };

  const handleClick = (e) => {
    setAnchorEl2(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl2(null);
    setSearchTerm(''); // 메뉴 검색 초기화
    LogoutLogic();
  };

  useEffect(() => {
    Init();
  }, []);

  useEffect(() => {
    if (!userInfo?.userid) return;

    const isAdmin = userInfo.authCd === 'ADMIN';
    if (isAdmin) {
      const ref = adminBaseInfoLoadedRef.current;
      if (ref.userId !== userInfo.userid) {
        adminBaseInfoLoadedRef.current = { userId: userInfo.userid, loaded: false };
      }
      if (adminBaseInfoLoadedRef.current.loaded) {
        return;
      }
      adminBaseInfoLoadedRef.current.loaded = true;
    }

    UpdateBaseInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, userInfo?.userid, userInfo?.authCd]);

  // 메뉴 검색시 드로워 열기 이펙트
  useEffect(() => {
    if (searchTerm.trim() && drawerStates.changedYn === 'Y') {
      dispatch(DrawerActions.OpenDrawer());
    }
  }, [searchTerm, drawerStates.changedYn, dispatch]);

  const GetGuideHideStorageKey = useCallback(
    (userId) => `${NEW_CONTRACT_GUIDE_HIDE_KEY}:${userId || 'anonymous'}`,
    [NEW_CONTRACT_GUIDE_HIDE_KEY]
  );

  const IsGuideHiddenForToday = useCallback(
    (userId) => {
      if (!userId) return false;
      try {
        const key = GetGuideHideStorageKey(userId);
        const raw = localStorage.getItem(key);
        if (!raw) return false;
        return dayjs().isBefore(dayjs(raw));
      } catch (e) {
        return false;
      }
    },
    [GetGuideHideStorageKey]
  );

  const SaveGuideHideForToday = useCallback(
    (userId, checked) => {
      if (!userId) return;
      try {
        const key = GetGuideHideStorageKey(userId);
        if (!checked) {
          localStorage.removeItem(key);
          return;
        }
        localStorage.setItem(key, dayjs().endOf('day').toISOString());
      } catch (e) {
        // localStorage 접근 오류는 무시
      }
    },
    [GetGuideHideStorageKey]
  );

  const HandleOpenNewContractGuide = useCallback(() => {
    setNewContractGuideHideToday(false);
    setNewContractGuideOpen(true);
  }, []);

  const HandleCloseNewContractGuide = useCallback(() => {
    SaveGuideHideForToday(userInfo?.userid, newContractGuideHideToday);
    setNewContractGuideOpen(false);
  }, [SaveGuideHideForToday, newContractGuideHideToday, userInfo?.userid]);

  const RowsFromSearchResponse = useCallback((res) => {
    return handleQuerySuccess(res, {});
  }, []);

  const CheckNewContractGuideAllCompleted = useCallback(async () => {
    const fallbackCustCd =
      custInfo?.[0]?.custCd || custInfo?.[0]?.CustCd || userInfo?.custCd || '';
    const coCd =
      userInfo?.authCd === 'ADMIN'
        ? searchInfo?.coCd || userInfo?.coCd || userInfo?.CoCd || ''
        : userInfo?.coCd || '';
    const custCd = searchInfo?.custCd || fallbackCustCd;
    // 판단에 필요한 컨텍스트가 부족하면 오탐으로 모달을 띄우지 않도록 스킵
    if (!userInfo?.userid || !coCd || !custCd) return true;

    const context = {
      userInfo: {
        ...userInfo,
        coCd,
      },
      search: {
        ...searchInfo,
        custCd,
      },
    };

    const [plantRes, memberRes, depRes] = await Promise.all([
      apiRequest(
        'post',
        Global.urls.Plant.getPlaBasicR1,
        mapParamsBySchema(PLA_BASIC_Schema.mapper.search1, context)
      ),
      apiRequest(
        'post',
        Global.urls.Member.getMemBasicR1,
        mapParamsBySchema(MEM_BASIC_Schema.mapper.search1, context)
      ),
      apiRequest(
        'post',
        Global.urls.Depart.getDepBasicR1,
        mapParamsBySchema(DEP_BASIC_Schema.mapper.search1, context)
      ),
    ]);

    const plantRows = RowsFromSearchResponse(plantRes);
    const memberRows = RowsFromSearchResponse(memberRes);
    const depRows = RowsFromSearchResponse(depRes);

    const workplaceRegistered =
      Array.isArray(plantRows) && plantRows.length > 0;
    const workforceRegistered =
      Array.isArray(memberRows) && memberRows.length > 0;
    const departmentRegistered = Array.isArray(depRows) && depRows.length > 0;

    if (!(workplaceRegistered && workforceRegistered && departmentRegistered)) {
      return false;
    }

    const depListRes = await apiRequest(
      'post',
      Global.urls.Utility.getDepLast,
      mapParamsBySchema(GON_BASIC_Schema.mapper.search1, context)
    );
    const depListRows = RowsFromSearchResponse(depListRes);
    const depCds = [
      ...new Set(
        (depListRows || [])
          .map((d) => d?.depCd ?? d?.DepCd ?? '')
          .filter((cd) => String(cd).trim().length > 0)
      ),
    ].slice(0, MAX_DEP_FOR_PROCESS_SCAN);

    let processRegistered = false;
    for (let i = 0; i < depCds.length; i += 1) {
      const depCd = depCds[i];
      const processRes = await apiRequest(
        'post',
        Global.urls.Gongjong.getGonBasicR1,
        mapParamsBySchema(GON_BASIC_Schema.mapper.search2, {
          ...context,
          depCd,
        })
      );
      const processRows = RowsFromSearchResponse(processRes);
      const hasProcess = Array.isArray(processRows) && processRows.length > 0;
      if (hasProcess) {
        processRegistered = true;
        break;
      }
    }
    return processRegistered;
  }, [RowsFromSearchResponse, userInfo, searchInfo, custInfo]);

  // 근로자의견 수렴 기간 플래그 (GetHomeData)
  useEffect(() => {
    if (!userInfo?.userid) return;
    const custCd = searchInfo?.custCd || userInfo?.custCd || '';
    if (!custCd) return;
    RunGetHomeData({ tbmYn: 'N' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.userid, userInfo?.custCd, searchInfo?.custCd]);

  useEffect(() => {
    if (guideAutoShownRef.current) return;
    if (location.pathname !== '/Home') return;
    if (!userInfo?.userid) return;
    guideAutoShownRef.current = true;
    if (IsGuideHiddenForToday(userInfo.userid)) return;
    (async () => {
      try {
        const allCompleted = await CheckNewContractGuideAllCompleted();
        if (!allCompleted) {
          HandleOpenNewContractGuide();
        }
      } catch (e) {
        HandleOpenNewContractGuide();
      }
    })();
  }, [
    location.pathname,
    userInfo?.userid,
    IsGuideHiddenForToday,
    CheckNewContractGuideAllCompleted,
    HandleOpenNewContractGuide,
  ]);

  // 메뉴·근로자의견 기간 플래그 변경 시 Drawer 메뉴 재구성
  useEffect(() => {
    RebuildMenuArrays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuInfo, userInfo?.authCd, riskWorkerOpinionMenuActive]);

  /** 기간 플래그 변경 시 GetInfo로 menuInfo 재동기화 (서버 menuInfo 필터와 동일) */
  const riskPeriodRefreshRef = useRef(false);
  useEffect(() => {
    if (!userInfo?.userid) return;
    if (!riskPeriodRefreshRef.current) {
      riskPeriodRefreshRef.current = true;
      return;
    }
    UpdateBaseInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riskWorkerOpinionMenuActive]);

  /** SUG/RIN 필터 후 URL과 menuInfo 불일치 시 활성 메뉴 또는 Home으로 이동 */
  const SUG_RIN_MENU_IDS = ['SuggestionList', 'RiskNearMiss'];
  useEffect(() => {
    if (!menuInfo?.length) return;
    const path = location.pathname.split('/').filter(Boolean)[0];
    if (!path || !SUG_RIN_MENU_IDS.includes(path)) return;
    if (menuInfo.some((m) => (m.menuId ?? m.MENU_ID) === path)) return;
    const fallback = menuInfo.find((m) =>
      SUG_RIN_MENU_IDS.includes(m.menuId ?? m.MENU_ID ?? '')
    );
    const targetId = fallback?.menuId ?? fallback?.MENU_ID;
    navigate(targetId ? `/${targetId}` : '/Home', { replace: true });
  }, [menuInfo, location.pathname, navigate]);

  // popper 열리는 시간 조절
  useEffect(() => {
    let timeout;

    if (drawerStates.open) {
      timeout = setTimeout(() => setDelayedDrawerOpen(true), 250); // 225ms보다 약간 여유
    } else {
      setDelayedDrawerOpen(false);
    }

    return () => clearTimeout(timeout);
  }, [drawerStates.open]);

  // 포그라운드 이벤트
  useEffect(() => {
    if (fcmReceptionPayload?.data) {
      const { body, responseData, gubun } = fcmReceptionPayload.data;
      dispatch(showInAppNoti({ gubun, responseData, body }));
    }
  }, [fcmReceptionPayload]);

  const Init = async () => {
    setLoading(true);
    try {
      GetMenuList();
      // GetMenuDescription();
      GetVersion();
    } catch (e) {
      console.log('Error: ', e);
      dispatch(showSnackbar({ message: e.message, type: 'error' }));
      //
    } finally {
      setLoading(false);
    }
  };

  const handleModalToggle = () => {
    setModalToggle(!modalToggle);
  };

  // 기본정보 업데이트 함수
  const UpdateBaseInfo = async () => {
    let upDateUserInfo = [];
    let menuInfo = [
      {
        NO: 0,
        AUTH_CD: '',
        AUTH_NM: '',
        DELETE_YN: 'N',
        PRO_ID: 'Home',
        MENU_NM: 'Home',
        MENU_TYPE: '0',
        MENU_UP: 'Home',
        PRINT_YN: 'N',
        SAVE_YN: 'N',
        SCREEN_TYPE: '0',
        SEQ: 0,
        open: false,
      },
    ];
    let lanInfo = [];
    let custInfo = [];
    let companyInfo = [];

    try {
      const fcmResult = await initFcm();
      const firebaseToken = fcmResult?.token || ''; // 구조분해 할당 시 토큰이 없을 경우 빈 문자열로 초기화
      const result = await Commons.Axios(
        'login',
        Global.base.useUrl + Global.urls.Account.GetInfo,
        {
          value1: Commons.EncryptAES('Kr'), // 언어팩 코드
          value2: userInfo.userid, // 사용자 아이디
          value3: Commons.EncryptAES(''),
          value4: Commons.EncryptAES(firebaseToken),
          value5: Commons.EncryptAES(Commons.IsMobile() ? 'Y' : 'N'),
          value6: Commons.EncryptAES(Commons.DeviceCheck()),
        }
      );
      let data = result.data;

      if (data.type === 'Error') {
        throw new Error(data.message);
      } else {
        if (data) {
          // 계약 기간: 일(날짜) 단위만 비교
          const today = dayjs().startOf('day');
          const contractStart = dayjs(
            data.companyInfo[0].contractStartDate
          ).startOf('day');
          const contractEnd = dayjs(
            data.companyInfo[0].contractEndDate
          ).startOf('day');
          if (
            today.isBefore(contractStart, 'day') ||
            today.isAfter(contractEnd, 'day')
          ) {
            LogoutLogic();
            dispatch(
              showSnackbar({
                message: '계약 기간이 아닙니다. 계약 기간을 확인해주세요.',
                type: 'warning',
              })
            );
            return;
          }

          upDateUserInfo.push(data.userInfo);
          for (let i = 0; i < data.menuInfo.length; i++) {
            menuInfo.push({ ...data.menuInfo[i], open: false });
          }
          menuInfo.push({
            NO: 999999,
            AUTH_CD: '',
            AUTH_NM: '',
            DELETE_YN: 'N',
            PRO_ID: 'Setting',
            MENU_NM: 'Setting',
            MENU_TYPE: '0',
            MENU_UP: 'Setting',
            PRINT_YN: 'N',
            SAVE_YN: 'N',
            SCREEN_TYPE: '0',
            SEQ: 99999999999,
            open: false,
          });
          for (let i = 0; i < data.lanInfo.length; i++) {
            lanInfo.push(data.lanInfo[i]);
          }
          for (let i = 0; i < data.custInfo.length; i++) {
            custInfo.push(data.custInfo[i]);
          }
          for (let i = 0; i < data.companyInfo.length; i++) {
            companyInfo.push(data.companyInfo[i]);
          }
          dispatch(BaseInfoActions.setUserInfo(upDateUserInfo[0]));
          dispatch(
            BaseInfoActions.setMenuInfo(
              menuInfo.filter((m) => {
                const id = m.menuId ?? m.MENU_ID ?? '';
                if (id === 'RiskNearMiss') return riskWorkerOpinionMenuActive;
                if (id === 'SuggestionList')
                  return !riskWorkerOpinionMenuActive;
                return true;
              })
            )
          );
          const resolvedUser = upDateUserInfo[0];
          dispatch(BaseInfoActions.setLanInfo(lanInfo));
          /** ADMIN: GetInfo custInfo는 갱신되지 않아 등록 직후 사업장이 누락됨 → 덮어쓰기 생략 */
          if (!IsAdminUser(resolvedUser)) {
            dispatch(BaseInfoActions.setCustInfo(custInfo));
          }
          dispatch(BaseInfoActions.setCompanyInfo(companyInfo));

          if (IsAdminUser(resolvedUser)) {
            const masterRows = await FetchAdminCompanyMasterRows(resolvedUser);
            if (masterRows.length > 0) {
              dispatch(BaseInfoActions.setCompanyInfo(masterRows));
            }
          }
        }
      }
    } catch (e) {
      console.log('Error: ', e);
      dispatch(showSnackbar({ message: e.message, type: 'error' }));
    }
  };

  const filterMenuList = (
    items,
    type,
    authCd,
    prefix = ['W2', 'BO'],
    excludeMenuIds = []
  ) => {
    return items
      .filter(
        (f) =>
          f.menuType === type &&
          // prefix.includes(f.PRO_ID.substring(0, 2)) &&
          f.screenType === '0' &&
          f.authCd === authCd &&
          !excludeMenuIds.includes(f.proId)
      )
      .sort((a, b) => a.SEQ - b.SEQ);
  };

  /** menuInfo / 권한 / 근로자의견 기간에 맞춰 Drawer 대·중메뉴 상태 갱신 */
  const RebuildMenuArrays = () => {
    if (!menuInfo?.length || !userInfo?.authCd) return;
    const { authCd } = userInfo;
    const isAdmin = authCd === 'ADMIN';
    const isBizOrHead = ['BUSINESS_OWNER', 'HEAD_MANAGER'].includes(authCd);
    const isGeneral = !isAdmin && !isBizOrHead;
    const menuPrefix = isGeneral ? ['W2'] : ['W2', 'BO'];
    const excludeIds = isAdmin || isBizOrHead ? ['W21700'] : [];

    const baseLarge = filterMenuList(
      menuInfo,
      '0',
      authCd,
      menuPrefix,
      excludeIds
    );
    const baseMedium = filterMenuList(menuInfo, '1', authCd, menuPrefix);

    const medium = baseMedium;
    let large = baseLarge;

    const mediumMenuUps = new Set(medium.map((m) => m.menuUp));
    large = large.filter((item) => {
      const hadAnyMedium = baseMedium.some((m) => m.menuUp === item.menuUp);
      if (!hadAnyMedium) return true;
      return mediumMenuUps.has(item.menuUp);
    });

    setLargeMenuArray(large);
    setMediumMenuArray(medium);
  };

  const GetMenuList = async () => {
    try {
      if (!userInfo) {
        return navigate('/ERROR', { state: { userNotInfo: true } });
      }

      // 유저 최신 정보 조회
      const res = await Commons.Axios(
        'post',
        Global.base.useUrl + Global.urls.Utility.getMember,
        {
          Userid: userInfo.userid,
        }
      );
      const apiRes = res.data;
      const { isSuccess, message, severity, data } = apiRes;
      if (!isSuccess) {
        dispatch(showSnackbar({ message: message, type: severity || 'error' }));
        return;
      }

      const photoPath = data?.photo || '';
      const { data: photoData } = await Commons.Axios(
        'post',
        Global.base.useUrl + Global.urls.Ftp.ftpResponse,
        {
          value1: Commons.EncryptAES(photoPath),
        }
      );

      if (photoData !== 'Error') {
        setImageFile({
          ...imageFile,
          mapFiles: `data:image/png;base64,${photoData}`,
          mapImage: {
            name: userInfo.photo?.split('/')?.pop() || '',
          },
        });
      }
      // 메뉴 정보 조회 및 필터링
      if (menuInfo?.length > 0) {
        RebuildMenuArrays();
      }
    } catch (e) {
      console.error('GetMenuList Error:', e);
      dispatch(
        showSnackbar({ message: e?.message ?? String(e), type: 'error' })
      );
    }
  };

  // 메뉴 설명 조회
  const GetMenuDescription = async () => {
    const result = await Commons.Axios(
      'post',
      Global.base.useUrl + Global.urls.Utility.getMenuDescription,
      {
        Userid: userInfo.userid,
      }
    );
    let data = result.data;

    if (data.length > 0) {
      if (data[0].type === 'Error') {
        dispatch(showSnackbar({ message: data[0].message, type: 'error' }));
        return false;
      } else {
        setDescription(data);
      }
    }
  };

  // 현재 버전 조회
  const GetVersion = async () => {
    try {
      const data = await RequestCheckClientVersion('post');
      const rows = ToManagerVersionRows(ParseCheckClientVersionResponse(data));
      if (rows.length > 0) {
        setVersion(rows);
      }
    } catch (e) {
      console.error('GetVersion error:', e);
    }
  };

  // Menu 뿌리는 로직 함수
  const CreateMenus = () => {
    return largeMenuArray.map((item) => {
      const mediumArr = mediumMenuArray.filter((f) => f.menuUp === item.menuUp);

      const hasChildren = mediumArr.length > 0;
      const proId = item.proId;

      if (!anchorRefs.current[proId]) {
        anchorRefs.current[proId] = React.createRef();
      }
      const anchorRef = anchorRefs.current[proId];
      return (
        <Box
          key={proId}
          sx={{ mb: '5px' }}
          onMouseEnter={hasChildren ? () => setHoveredMenuId(proId) : undefined}
          onMouseLeave={hasChildren ? () => setHoveredMenuId(null) : undefined}
        >
          <ListItemButton
            ref={anchorRef}
            onMouseEnter={() => setHoveredMenuId(proId)}
            onMouseLeave={() => setHoveredMenuId(null)}
            onClick={() => {
              setActiveMenuId(proId);
              if (!hasChildren) {
                if (['Home', 'BasicInfo'].includes(proId)) {
                  navigate('/Home');
                } else if (['Logout'].includes(proId)) {
                  LogoutLogic();
                } else {
                  navigate(`${item.menuUp}`);
                }
              }
            }}
            sx={{
              px: 0,
              ml: 1,
              borderRadius: '8px 0px 0px 8px',
              backgroundColor:
                hoveredMenuId === proId ? 'rgb(252, 252, 252)' : 'transparent',
              border:
                hoveredMenuId === proId
                  ? '1px solid #2EB450'
                  : '1px solid transparent',
              borderRight: 0,
              transition: 'background-color 0.1s',
              '&:hover': {
                backgroundColor: 'rgb(252, 252, 252)',
                border: 1,
                borderRight: 0,
                borderColor: '#2EB450',
              },
            }}
          >
            <ListItemIcon
              sx={{
                px: '20px',
                minWidth: 41,
                color:
                  activeMenuId === proId && !drawerStates.open // 선택된페이지 + 드로워 닫혔을때
                    ? '#2EB450'
                    : !drawerStates.open // 드로워 닫혔을때
                      ? '#B3B3B3'
                      : hoveredMenuId === proId // 호버됐을때
                        ? '#2EB450'
                        : '#FEF4F4',
                fontWeight: hoveredMenuId === proId ? 600 : 500,
                '& svg': {
                  width: '30px',
                  height: '30px',
                },
              }}
            >
              {iconMap[item.menuId]}
            </ListItemIcon>
            <ListItemText
              disableTypography
              primary={
                <Typography
                  sx={{
                    fontSize: '1.2rem',
                    color: !drawerStates.open
                      ? '#B3B3B3'
                      : hoveredMenuId === proId
                        ? '#2EB450'
                        : '#FEF4F4',
                    fontWeight: hoveredMenuId === proId ? 600 : 500,
                  }}
                >
                  {item.menuNm}
                </Typography>
              }
            />
          </ListItemButton>

          {hasChildren && (
            <Popper
              // open={true}
              open={hoveredMenuId === proId && delayedDrawerOpen}
              anchorEl={anchorRef.current}
              placement="right-start"
              sx={{
                zIndex: 1200,
                borderRadius: '0px 8px 8px 0px',
                backgroundColor: 'rgb(252, 252, 252)',
                border: 1,
                borderLeft: 0,
                borderColor: '#2EB450',
              }}
            >
              <Box
                onMouseEnter={() => setHoveredMenuId(proId)}
                onMouseLeave={() => setHoveredMenuId(null)}
              >
                <MenuList>
                  {mediumArr.map((subItem) => (
                    <MenuItem
                      key={subItem.proId}
                      onClick={() => {
                        setActiveMenuId(subItem.menuUp);
                        setActiveSubMenuId(subItem.proId);
                        navigate(`${subItem.menuId}`);
                        setHoveredMenuId(null);
                        if (drawerStates.changedYn === 'Y') {
                          dispatch(DrawerActions.CloseDrawer());
                          drawerRef.current.focus();

                          setLargeMenuArray(
                            largeMenuArray.map((item) => ({
                              ...item,
                              open: false,
                            }))
                          );
                        }
                      }}
                      sx={{
                        fontWeight:
                          activeSubMenuId === subItem.proId ? 600 : 500,
                        color: activeSubMenuId === subItem.proId && '#2EB450',
                        '&:hover': {
                          fontWeight: 600,
                          color: '#2EB450',
                        },
                      }}
                    >
                      {subItem.menuNm}
                    </MenuItem>
                  ))}
                </MenuList>
              </Box>
            </Popper>
          )}
        </Box>
      );
    });
  };

  // #region 메뉴 검색
  const HighlightText = (text, keyword) => {
    if (!text || !keyword) return text;
    const lowerText = text.toLowerCase();
    const lowerKeyword = keyword.toLowerCase();
    const index = lowerText.indexOf(lowerKeyword);
    if (index === -1) return text;

    return (
      <>
        {text.substring(0, index)}
        <Box component="span" sx={{ backgroundColor: '#ffff00' }}>
          {text.substring(index, index + keyword.length)}
        </Box>
        {text.substring(index + keyword.length)}
      </>
    );
  };

  const GetMenuDisplayName = (item) => item?.menuNm ?? '';

  const HandleSearchMenuClick = (item) => {
    if (item.isLogout) {
      setSearchTerm('');
      LogoutLogic();
      return;
    }

    setActiveMenuId(item.parentProId);
    if (item.subProId) setActiveSubMenuId(item.subProId);
    navigate(item.path);
    setSearchTerm('');
    setHoveredMenuId(null);
    dispatch(DrawerActions.CloseDrawer());
  };

  const RenderSearchResults = () => {
    const keyword = searchTerm.toLowerCase().trim();
    if (!keyword) return null;

    const results = [];

    largeMenuArray.forEach((large) => {
      const children = mediumMenuArray.filter((m) => m.menuUp === large.menuUp);
      const largeName = GetMenuDisplayName(large);
      const largeMatch = largeName.toLowerCase().includes(keyword);

      if (children.length === 0) {
        if (largeMatch) {
          results.push({
            key: large.proId,
            label: largeName,
            parentLabel: null,
            path: ['Home', 'BasicInfo'].includes(large.proId)
              ? '/Home'
              : `${large.menuUp}`,
            parentProId: large.proId,
            subProId: null,
            isLogout: large.proId === 'Logout',
          });
        }
        return;
      }

      children.forEach((sub) => {
        const subName = GetMenuDisplayName(sub);
        if (largeMatch || subName.toLowerCase().includes(keyword)) {
          results.push({
            key: sub.proId,
            label: subName,
            parentLabel: largeName,
            path: `${sub.menuId}`,
            parentProId: sub.menuUp,
            subProId: sub.proId,
            isLogout: false,
          });
        }
      });
    });

    if (results.length === 0) {
      return (
        <Typography sx={{ px: 2, py: 1, color: '#FEF4F4' }} variant="body2">
          검색 결과가 없습니다.
        </Typography>
      );
    }

    return results.map((item) => (
      <ListItemButton
        key={item.key}
        onClick={() => HandleSearchMenuClick(item)}
        sx={{
          px: 2,
          borderRadius: '8px',
          '&:hover': {
            backgroundColor: 'rgb(252, 252, 252)',
          },
        }}
      >
        <ListItemText
          primary={
            item.parentLabel ? (
              <Typography sx={{ fontSize: '1rem', color: '#FEF4F4' }}>
                <Typography
                  component="span"
                  sx={{ color: '#B3B3B3', fontSize: '0.9rem' }}
                >
                  {HighlightText(item.parentLabel, searchTerm)}
                  {' / '}
                </Typography>
                {HighlightText(item.label, searchTerm)}
              </Typography>
            ) : (
              <Typography sx={{ fontSize: '1rem', color: '#FEF4F4' }}>
                {HighlightText(item.label, searchTerm)}
              </Typography>
            )
          }
        />
      </ListItemButton>
    ));
  };

  const RenderMenus = () => {
    if (searchTerm.trim()) return RenderSearchResults();
    return CreateMenus();
  };
  // #endregion

  const getMenuNmById = (menuId) => {
    if (!menuId || !menuInfo?.length) return '';
    const row = menuInfo.find((f) => (f.menuId ?? f.MENU_ID) === menuId);
    return row?.menuNm ?? row?.MENU_NM ?? '';
  };

  const CurrentPage = () => {
    let splitArr = window.location.href.split('/');
    if (splitArr[3] === 'Home') {
    } else {
      if (splitArr.length === 4) {
        const descRow = description.find(
          (f) => f.menuId === splitArr[3] && f.descriptionYn === 'Y'
        );
        return (
          <Typography sx={{}} fontSize={'22px'} fontWeight={'bold'}>
            {getMenuNmById(splitArr[3])}
            {descRow && (
              <GreenTooltip title={descRow?.description}>
                <IconButton>
                  <HelpIcon color="info" />
                </IconButton>
              </GreenTooltip>
            )}
          </Typography>
        );
      } else if (splitArr.length === 5) {
        if (splitArr[3] === splitArr[4]) {
          return [
            <Typography
              sx={{}}
              fontSize={'1.2rem'}
              fontWeight={'bold'}
              key={splitArr[3]}
            >
              {getMenuNmById(splitArr[3])}
            </Typography>,
          ];
        } else {
          if (menuInfo.filter((f) => f.menuId === splitArr[4]).length === 0) {
            return [
              <Typography
                fontSize={'1.2rem'}
                fontWeight={'bold'}
                key={splitArr[3]}
              >
                {getMenuNmById(splitArr[3])}
              </Typography>,
            ];
          } else {
            return [
              <Typography
                fontSize={'1.2rem'}
                fontWeight={'bold'}
                key={splitArr[3]}
              >
                {getMenuNmById(splitArr[3])}
              </Typography>,
              <Typography
                fontSize={'1.2rem'}
                fontWeight={'bold'}
                key={splitArr[4]}
              >
                {getMenuNmById(splitArr[4])}
              </Typography>,
            ];
          }
        }
      }
    }
  };

  // 로그아웃 함수 (서버 LogOut → ClearMemberFcmAsync, Account_ClearMemberFcm 호출). LoginFunction 로그인 흐름 참고.
  const LogoutLogic = async () => {
    try {
      // 서버: UserModel camelCase(id, coCd, fcmToken, deviceType). Account_ClearMemberFcm는 FCM_TOKEN='0' 고정, WHERE @CoCd, @UserId(=Id), @DeviceType(빈값이면 해당 디바이스 타입 무시).
      const logoutParams = {
        id: userInfo.userid ?? userInfo.userId ?? '',
        coCd: Commons.EncryptAES(userInfo.coCd ?? ''),
        fcmToken: '0',
        deviceType: Commons.EncryptAES(Commons.DeviceCheck() ?? ''),
      };
      const response = await Commons.Axios(
        'logout',
        Global.base.useUrl + Global.urls.Account.logout,
        logoutParams
      );
      const data = response?.data;
      if (!data || (data?.Result !== 'Success' && data?.result !== 'Success')) {
        dispatch(
          showSnackbar({
            message: data?.message ?? '로그아웃 처리에 실패했습니다.',
            type: 'warning',
          })
        );
        return false;
      }
      dispatch(BaseInfoActions.setRiskWorkerOpinionMenuActive(false));
      Commons.Storages('set', 'auth', { auth: false });
      navigate('/', { state: { contract: 'N' } });
    } catch (e) {
      const message =
        e?.response?.data ??
        e?.message ??
        '로그아웃 처리 중 오류가 발생했습니다.';
      dispatch(showSnackbar({ message: String(message), type: 'error' }));
    }
  };

  // 프로필 사진 가져오기
  const updateProfile = async (_item) => {
    try {
      await Commons.Axios(
        'post',
        Global.base.useUrl + Global.urls.Execute.ftpResponse,
        {
          value1: Commons.EncryptAES(_item),
        }
      ).then((result) => {
        let data = result.data;
        if (data !== 'Error') {
          setImageFile({
            ...imageFile,
            mapFiles: `data:image/png;base64,${data}`,
            mapImage: {
              name: _item.split('/')[_item.split('/').length - 1],
            },
          });
        } else {
          setImageFile({
            ...imageFile,
            mapFiles: '',
            mapImage: {
              name: '',
            },
          });
        }
      });
    } catch (e) {
      console.log(e);
    } finally {
    }
  };
  // console.log(userInfo);

  // 문의하기 전송
  const SendLogic = async () => {
    setLoading(true);
    let emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    try {
      if (!qnaInput.sendEmail?.trim()) {
        dispatch(
          showSnackbar({
            message: '수신받을 이메일을 입력하세요.',
            type: 'warning',
          })
        );
      } else if (!qnaInput.title?.trim()) {
        dispatch(
          showSnackbar({
            message: '제목을 입력하세요.',
            type: 'warning',
          })
        );
      } else if (!qnaInput.content?.trim()) {
        dispatch(
          showSnackbar({
            message: '내용을 입력하세요.',
            type: 'warning',
          })
        );
      } else if (!emailRegex.test(qnaInput.sendEmail?.trim())) {
        dispatch(
          showSnackbar({
            message: '이메일 형식을 다시 확인 하세요.',
            type: 'warning',
          })
        );
      } else if (!checkedTerms.terms1) {
        dispatch(
          showSnackbar({
            message: '문의 접수를 위해 개인정보 수집·이용에 동의해주세요.',
            type: 'warning',
          })
        );
      } else {
        await Commons.Axios(
          'post1',
          Global.base.useUrl + Global.urls.SendMail.sendMail,
          {
            from: Commons.EncryptAES(qnaInput.sendEmail) || '',
            subject: Commons.EncryptAES(qnaInput.title) || '',
            body: {
              id: Commons.EncryptAES('1. ID: ' + qnaInput.id) || '',
              name:
                Commons.EncryptAES(
                  '2. 이름: ' + qnaInput.name + ' / 연락처: ' + qnaInput.contact
                ) || '',
              type:
                Commons.EncryptAES(
                  qnaInput.type === 'itemQna'
                    ? '3. 구분: 제품문의'
                    : '3. 구분: 기술문의'
                ) || '',
              contents:
                Commons.EncryptAES('4. 문의내용: ' + qnaInput.content) || '',
            },
          }
        ).then((result) => {
          let data = result.data;
          if (data) {
            if (data.type === Commons.TypeSuccess()) {
              setModalToggle(false);
              setQnaInput((preState) => ({
                ...preState,
                sendEmail: '',
                type: 'itemQna',
                title: '',
                content: '',
              }));

              dispatch(
                showSnackbar({
                  message: '메일이 정상 전송되었습니다.',
                  type: 'success ',
                })
              );
            } else {
              throw new Error(data.message ? data.message : '');
            }
          }
        });
      }
    } catch (e) {
      console.log('Error: ', e);
    } finally {
      setLoading(false);
    }
  };

  const isFormValid =
    qnaInput.id?.trim() !== '' &&
    qnaInput.name?.trim() !== '' &&
    qnaInput.contact?.trim() !== '' &&
    qnaInput.sendEmail?.trim() !== '' &&
    qnaInput.title?.trim() !== '' &&
    qnaInput.content?.trim() !== '';

  const SaveLogic1 = async () => {
    setLoading(true);
    let passwordRegex = /^(?=.*[a-zA-Z])(?=.*\d).{8,}$/;

    try {
      if (!inputs.currentPassword) {
        dispatch(
          showSnackbar({
            message: '현재 비밀번호를 입력하세요.',
            type: 'warning',
          })
        );
      } else if (!inputs.password) {
        dispatch(
          showSnackbar({
            message: '비밀번호를 입력하세요.',
            type: 'warning',
          })
        );
      } else if (!inputs.passwordConfirm) {
        dispatch(
          showSnackbar({
            message: '비밀번호 확인을 입력하세요.',
            type: 'warning',
          })
        );
      } else if (inputs.password !== inputs.passwordConfirm) {
        dispatch(
          showSnackbar({
            message: '비밀번호가 다릅니다.',
            type: 'warning',
          })
        );
      } else if (!passwordRegex.test(inputs.password)) {
        dispatch(
          showSnackbar({ message: '비밀번호를 확인 하세요.', type: 'warning' })
        );

        return false;
      } else {
        await Commons.Axios(
          'post',
          Global.base.useUrl + Global.urls.Account.ChangePassword,
          {
            UserId: userInfo.userid,
            CurrentPassword: Commons.EncryptAES(inputs.currentPassword),
            NewPassword: Commons.EncryptAES(inputs.passwordConfirm),
          }
        ).then((result) => {
          let data = result.data;

          if (data && data.type === 'Ok') {
            dispatch(
              showSnackbar({
                message: '변경되었습니다.',
                type: 'success',
              })
            );
            setFindDialog(false);
          } else {
            throw new Error(data?.message);
          }
        });
      }
    } catch (e) {
      console.log('Error: ', e);
      dispatch(showSnackbar({ message: e.message, type: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  const checkCapsLock = (e) => {
    let capsLock = e.getModifierState('CapsLock');
    setCapsLockFlag(capsLock);
  };

  return (
    <div className="Drawer">
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            backgroundColor: '#fff',
            height: appbarHeight,
            justifyContent: 'center',
          }}
        >
          <Toolbar
            sx={{
              pl: '20px !important',
              pr: '30px !important',
            }}
          >
            {/* 왼쪽 로고 */}
            <IconButton
              sx={{
                p: 0, // 기본 패딩 제거
                m: 0, // 마진도 제거
                borderRadius: 0,
                height: 'auto', // 강제로 높이 제한 없애기
                display: 'flex',
                alignItems: 'center', // 내부 수직 정렬
              }}
              edge="start"
              onClick={() => {
                if (drawerStates.changedYn === 'Y') {
                  navigate('/Home');
                  setActiveMenuId('W20001');
                  setActiveSubMenuId(null);
                }
              }}
            >
              <img
                style={{
                  height: '60px',
                  display: 'block', // inline-block 기본값 제거
                }}
                src={location.pathname === '/Home' ? LogoImgHome : LogoImgPage}
                alt="logo"
              />
            </IconButton>
            <Breadcrumbs
              sx={{
                color: '#534c4c',
                '& .MuiBreadcrumbs-separator': {
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiTypography-root': {
                  fontSize: '1.625rem',
                },
              }}
            >
              {CurrentPage()}
            </Breadcrumbs>

            {/* 중앙 여백 */}
            <Box sx={{ flex: 1 }} />

            {/* 오른쪽 검색창 + 프로필 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              {/* <SearchBox
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              /> */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 0.25,
                  borderRadius: '10px',
                  px: 0.5,
                  py: 0.5,
                }}
              >
                {[
                  {
                    key: 'manual',
                    label: '매뉴얼',
                    title: '관리자 매뉴얼',
                    onClick: () => {
                      void openAdminManual();
                    },
                    disabled: manualChecking,
                    icon: (
                      <MenuBookIcon sx={{ fontSize: 22, color: '#444444' }} />
                    ),
                  },
                  {
                    key: 'history',
                    label: '처리 내역',
                    title: '처리 내역',
                    onClick: () => setHistoryDialogOpen(true),
                    icon: (
                      <HistoryIcon sx={{ fontSize: 22, color: '#444444' }} />
                    ),
                  },
                  {
                    key: 'guide',
                    label: '가이드',
                    title: '신규 계약 운영 가이드 (시스템·고객사 관리자 공통)',
                    onClick: HandleOpenNewContractGuide,
                    icon: (
                      <AutoStoriesIcon
                        sx={{ fontSize: 22, color: '#444444' }}
                      />
                    ),
                  },
                  {
                    key: 'inquiry',
                    label: '문의',
                    title: '문의하기',
                    onClick: handleModalToggle,
                    icon: <HelpIcon sx={{ fontSize: 22, color: '#444444' }} />,
                  },
                ].map((item) => (
                  <Box
                    key={item.key}
                    component="button"
                    type="button"
                    title={item.title}
                    disabled={item.disabled ?? false}
                    onClick={item.onClick}
                    sx={{
                      all: 'unset',
                      cursor: item.disabled ? 'wait' : 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1,
                      py: 0.5,
                      borderRadius: '8px',
                      boxSizing: 'border-box',
                      // all: unset 이후 Toolbar 글꼴 크기를 다시 물려받아야 자식 em 단위가 맞음
                      fontSize: 'inherit',
                      fontFamily: 'inherit',
                      lineHeight: 'inherit',
                      ...(item.disabled
                        ? { opacity: 0.55, pointerEvents: 'none' }
                        : {}),
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.06)',
                      },
                    }}
                  >
                    {item.icon}
                    <Box
                      component="span"
                      sx={{
                        fontSize: '1.1em',
                        fontWeight: 500,
                        color: '#444444',
                        lineHeight: 1.2,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.label}
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* 프로필 */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                }}
              >
                {imageFile?.mapFiles ? (
                  <Avatar
                    src={imageFile?.mapFiles}
                    sx={{
                      width: '48px',
                      height: '48px',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '48px',
                      height: '48px',
                      backgroundColor: '#7B91A2',
                      borderRadius: '50%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Avatar
                      src={avata}
                      sx={{
                        width: '24px',
                        height: '24px',
                      }}
                    />
                  </Box>
                )}
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: '#9BB3C5', fontSize: '0.875em' }}
                  >
                    {/* 안전총괄책임자 */}
                  </Typography>
                  <Stack direction="row">
                    <Typography
                      variant="body2"
                      sx={{
                        cursor: 'pointer',
                        color: '#333333',
                        fontWeight: 500,
                      }}
                      onClick={handleClick}
                    >
                      {userInfo?.userNm ?? ''}
                    </Typography>
                    <Box
                      sx={{
                        color: '#333333',
                        pl: 1,
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                      onClick={handleClick}
                    >
                      <Icon.DropdDown />
                    </Box>
                  </Stack>
                </Box>
              </Box>

              {/* 드롭다운 메뉴 */}
              <Menu
                id="basic-menu"
                anchorEl={anchorEl2}
                open={open2}
                onClose={() => setAnchorEl2(null)}
                MenuListProps={{ 'aria-labelledby': 'avatar-button' }}
              >
                {/* <MenuItem onClick={() => navigate('/MyPage')}>
                  <ListItemIcon>
                    <SettingsIcon />
                  </ListItemIcon>
                  MY PAGE
                </MenuItem> */}
                <MenuItem onClick={handleClose}>
                  <ListItemIcon>
                    <LogoutIcon />
                  </ListItemIcon>
                  로그아웃
                </MenuItem>
                <MenuItem onClick={() => setFindDialog(true)}>
                  <ListItemIcon>
                    <Icon.Password2 />
                  </ListItemIcon>
                  비밀번호 변경
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>
        <DrawerBody
          ref={drawerRef}
          variant="permanent"
          anchor="left"
          open={drawerStates.open}
          sx={{
            '& .MuiDrawer-paper': {
              overflowY: 'auto',
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            },
          }}
          onMouseEnter={() => {
            if (drawerStates.changedYn === 'Y') {
              dispatch(DrawerActions.OpenDrawer());
              drawerRef.current?.focus();
            }
          }}
          onMouseLeave={() => {
            if (drawerStates.changedYn === 'Y') {
              dispatch(DrawerActions.CloseDrawer());
              drawerRef.current.focus();

              setLargeMenuArray(
                largeMenuArray.map((item) => ({
                  ...item,
                  open: false,
                }))
              );
            }
          }}
        >
          <DrawerHeader />
          <ScrollWrapper
            showScroll={drawerStates.open && showScroll}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {/* Drawer content here */}
            <List sx={{ pt: '40px' }}>{RenderMenus()}</List>
            {drawerStates.open && version && (
              <Typography
                sx={{
                  color: '#FFF',
                  fontSize: '0.75em !important',
                  textAlign: 'start',
                  ml: '12px',
                  alignItems: 'end',
                }}
              >
                {version.find((f) => f.type === 'MANAGER').version}
              </Typography>
            )}
          </ScrollWrapper>
        </DrawerBody>
      </Box>
      <Center
        open={drawerStates.open}
        onFocus={() => dispatch(DrawerActions.CloseDrawer())}
      >
        <Box
          component="main"
          sx={{
            height:
              // isDashboard
              // ? `calc(1080px - ${appbarHeight})`
              // :
              '100%',
            borderTopLeftRadius: '30px',
            backgroundColor: '#F2F6F9',
          }}
        >
          <Box
            maxWidth={'100%'}
            sx={{
              px: isDashboard ? 0 : '24px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflowX: 'auto', // 가로 스크롤 추가
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mt: isDashboard ? 0 : 1,
                p: isDashboard ? 0 : 1,
              }}
            ></Box>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <Outlet context={{ updateProfile }} />
            </Box>
          </Box>
        </Box>
      </Center>
      {/* 처리 내역 히스토리 다이얼로그 */}
      <OperationHistoryDialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
      />
      <NewContractGuideDialog
        open={newContractGuideOpen}
        onClose={HandleCloseNewContractGuide}
        onSave={HandleCloseNewContractGuide}
        dontShowToday={newContractGuideHideToday}
        onToggleDontShowToday={setNewContractGuideHideToday}
      />

      <Dialog
        title="매뉴얼"
        open={manualUnavailableOpen}
        onClose={() => setManualUnavailableOpen(false)}
        maxWidth="sm"
        fullWidth
        useSaveButton={false}
        closeButtonName="닫기"
        content={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography
              variant="body2"
              sx={{ color: '#534c4c', whiteSpace: 'pre-line' }}
            >
              {manualUnavailableMessage}
            </Typography>
          </Box>
        }
      />
      {/* 문의하기 다이어로그 */}
      {modalToggle && (
        <Dialog
          title={'문의하기'}
          open={modalToggle}
          onClose={(e) => setModalToggle(false)}
          contentSx={{ display: 'flex', flexDirection: 'column' }}
          fullWidth
          actions={
            <Box
              sx={{
                width: '50%',
                display: 'flex',
                gap: 1,
              }}
            >
              <Button
                fullWidth
                variant="contained"
                sx={{
                  mt: 3,
                  backgroundColor: '#F3F7F8',
                  color: '#000',
                  fontSize: '16px',
                }}
                onClick={(e) => {
                  setModalToggle(false);
                  setQnaInput((preState) => ({
                    ...preState,
                    sendEmail: '',
                    type: 'itemQna',
                    title: '',
                    content: '',
                  }));
                }}
                name="닫기"
              />
              <Button
                fullWidth
                variant="contained"
                sx={{
                  mt: 3,
                  backgroundColor: isFormValid ? '#2CA900' : '#ccc',
                  fontSize: '16px',
                }}
                onClick={(e) => {
                  SendLogic();
                }}
                name="보내기"
                disabled={!isFormValid}
              />
            </Box>
          }
          saveButtonName={'보내기'} //저장
          closeButtonName={'닫기'} //닫기
          useCloseButton={false}
          useSaveButton={false}
          useSaveAfterClose={false}
          maxWidth="md"
          content={
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  fontSize: '1rem',
                }}
              >
                <p>
                  접수된 문의는
                  <span style={{ color: '#f33629ff' }}> 2~3일 이내</span>에
                  답변드립니다.
                </p>
                <p>
                  문의 내용을 구체적으로 작성해 주시면 더 빠른 상담이
                  가능합니다.
                </p>
                <p> 남겨주신 연락처와 이메일로 답변을 드립니다.</p>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    minWidth: 50,
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    mb: 1,
                  }}
                >
                  작성자 정보
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    mb: 1,
                  }}
                >
                  <Typography
                    sx={{
                      minWidth: 50,
                      fontSize: '1.2rem',
                      ml: 1,
                    }}
                  >
                    <span style={{ color: 'red' }}>* </span> ID
                  </Typography>
                  <TextField
                    fullWidth
                    value={qnaInput.id}
                    onChange={(e) => {
                      setQnaInput({
                        ...qnaInput,
                        id: e.target.value,
                      });
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                    gap: 5,
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      width: '100%',
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: 50,
                        fontSize: '1.2rem',
                        ml: 1,
                      }}
                    >
                      <span style={{ color: 'red' }}>* </span>
                      이름
                    </Typography>
                    <TextField
                      fullWidth
                      value={qnaInput.name}
                      onChange={(e) => {
                        setQnaInput({
                          ...qnaInput,
                          name: e.target.value,
                        });
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      width: '100%',
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: 50,
                        fontSize: '1.2rem',
                        ml: 1,
                      }}
                    >
                      <span style={{ color: 'red' }}>* </span>
                      연락처
                    </Typography>
                    <TextField
                      fullWidth
                      value={qnaInput.contact}
                      onChange={(e) => {
                        setQnaInput({
                          ...qnaInput,
                          contact: e.target.value,
                        });
                      }}
                    />
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    mb: 1,
                  }}
                >
                  <Typography
                    sx={{
                      minWidth: 50,
                      fontSize: '1.2rem',
                      ml: 1,
                    }}
                  >
                    <span style={{ color: 'red' }}>* </span>
                    이메일 주소
                  </Typography>
                  <TextField
                    fullWidth
                    value={qnaInput.sendEmail}
                    onChange={(e) => {
                      setQnaInput({
                        ...qnaInput,
                        sendEmail: e.target.value,
                      });
                    }}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    minWidth: 50,
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                  }}
                >
                  <span style={{ color: 'red' }}>* </span>
                  유형
                </Typography>

                <Combo
                  fullWidth
                  items={[
                    {
                      name: '제품문의',
                      value: 'itemQna',
                    },
                    {
                      name: '기술문의',
                      value: 'techQna',
                    },
                  ]}
                  value={qnaInput.type}
                  onChange={(e) => {
                    setQnaInput({
                      ...qnaInput,
                      type: e,
                    });
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    minWidth: 50,
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                  }}
                >
                  <span style={{ color: 'red' }}>* </span>
                  제목
                </Typography>

                <TextField
                  fullWidth
                  value={qnaInput.title}
                  onChange={(e) => {
                    setQnaInput({
                      ...qnaInput,
                      title: e.target.value,
                    });
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    minWidth: 50,
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                  }}
                >
                  {' '}
                  <span style={{ color: 'red' }}>* </span>
                  문의 내용
                </Typography>

                <Box
                  sx={{
                    height: '100%',
                    overflow: 'auto',
                    width: '100%',
                  }}
                >
                  <TextField
                    fullWidth
                    value={qnaInput.content}
                    onChange={(e) => {
                      if (e.target.value.length <= 1000) {
                        setQnaInput({
                          ...qnaInput,
                          content: e.target.value,
                        });
                      } else {
                        setQnaInput({
                          ...qnaInput,
                          content: e.target.value.substr(0, 1000),
                        });

                        dispatch(
                          showSnackbar({
                            message: '문의내용은 1000자까지 입력 가능합니다.',
                            type: 'error',
                          })
                        );
                      }
                    }}
                    multiline={true}
                    rows={8}
                    InputProps={{
                      style: {
                        alignItems: 'flex-start',
                      },
                    }}
                  />
                  <Typography variant="body2" sx={{ textAlign: 'right' }}>
                    ({qnaInput.content.length}/1000)
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Checkbox
                  name="* 문의 접수 및 답변을 위해 개인정보(이름, 연락처, 이메일)를 수집·이용하는 데 동의합니다."
                  checked={checkedTerms.terms1}
                  onChange={(val) =>
                    setCheckedTerms({ ...checkedTerms, terms1: val })
                  }
                />
              </Box>
            </Box>
          }
        />
      )}

      <Dialog
        title={'비밀번호 변경'}
        titleSx={{ fontWeight: 'bold' }}
        open={findDialog}
        onClose={() => {
          setInputs({
            ...inputs,
            currentPassword: '',
            password: '',
            passwordConfirm: '',
          });
          setFindDialog(false);
        }}
        contentSx={{ display: 'flex', flexDirection: 'column' }}
        PaperProps={{ sx: { borderRadius: 4 } }}
        fullWidth
        useCloseButton={false}
        useSaveButton={false}
        closeButtonName={'닫기'}
        saveButtonName={'확인'}
        onSave={() => SaveLogic1()}
        maxWidth="sm"
        content={
          <Box sx={{ p: 1 }}>
            <Stack spacing={2}>
              <TextField
                label={'현재 비밀번호'}
                value={inputs.currentPassword ? inputs.currentPassword : ''}
                onChange={(e) => {
                  setInputs({
                    ...inputs,
                    currentPassword: e.target.value,
                  });
                }}
                onKeyUp={(e) => checkCapsLock(e)}
                type="password"
              />
              <TextField
                fullWidth
                label={'비밀번호'}
                value={inputs.password ? inputs.password : ''}
                onChange={(e) => {
                  setInputs({
                    ...inputs,
                    password: e.target.value,
                  });
                }}
                onKeyUp={(e) => checkCapsLock(e)}
                type="password"
              />
              <TextField
                fullWidth
                label={'비밀번호 확인'}
                value={inputs.passwordConfirm ? inputs.passwordConfirm : ''}
                onChange={(e) => {
                  setInputs({
                    ...inputs,
                    passwordConfirm: e.target.value,
                  });
                }}
                onKeyUp={(e) => checkCapsLock(e)}
                type="password"
              />
              {capsLockFlag && (
                <Typography variant="caption" color="red">
                  {`Caps Lock이 켜져 있습니다`}
                </Typography>
              )}
              <Typography variant="caption">
                {`(영문, 숫자 포함 8자 이상으로 입력 해주세요.)`}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginY: 1.5,
                  marginX: 1,
                }}
              >
                <Button
                  sx={{
                    marginX: 0.5,
                    width: '100%',
                    borderRadius: 2,
                    backgroundColor: '#E0EAE9',
                    '&:hover': {
                      backgroundColor: '#E0EAE9', // hover 시 배경색
                    },
                    color: '#00602E',
                    fontSize: '15px',
                    padding: '0px, 20px, 0px, 20px',
                  }}
                  size="medium"
                  name={'취소'}
                  onClick={() => {
                    setInputs({
                      ...inputs,
                      currentPassword: '',
                      password: '',
                      passwordConfirm: '',
                    });
                    setFindDialog(false);
                  }}
                ></Button>
                <Button
                  sx={{
                    marginX: 0.5,
                    width: '100%',
                    borderRadius: 2,
                    backgroundColor: '#2CA900',
                    '&:hover': {
                      backgroundColor: '#2CA900', // hover 시 배경색
                    },
                    color: '#FFFFFF',
                    //height: "5vh",
                    fontSize: '15px',
                    padding: '0px, 20px, 0px, 20px',
                  }}
                  size="medium"
                  name={'저장'}
                  onClick={() => {
                    SaveLogic1();
                    setInputs({
                      ...inputs,
                      currentPassword: '',
                      password: '',
                      passwordConfirm: '',
                    });
                  }}
                ></Button>
              </Box>
            </Stack>
          </Box>
        }
      />
    </div>
  );
}
